import { useEffect, useRef, useState } from 'react'
import { Tooltip } from '@/components/ui/Tooltip'
import { gravatarUrl } from '@/utils/gravatar'

export interface AvatarStackItem {
  id: string | number
  name: string
  email?: string
  color?: string
}

interface AvatarStackProps {
  items: AvatarStackItem[]
  max?: number
  size?: number
  showPopover?: boolean
  ringColor?: string
  totalCount?: number
}

const PALETTE = [
  '#6366f1', '#8b5cf6', '#ec4899', '#f59e0b',
  '#10b981', '#3b82f6', '#ef4444', '#14b8a6',
]

export function avatarColor(seed: string | number): string {
  const str = String(seed)
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0
  }
  return PALETTE[hash % PALETTE.length]
}

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

function StackItem({
  item,
  size,
  ringColor,
  index,
  max,
}: {
  item: AvatarStackItem
  size: number
  ringColor: string
  index: number
  max: number
}) {
  const [src, setSrc] = useState<string | null>(null)
  const [imgFailed, setImgFailed] = useState(false)
  const bg = item.color ?? avatarColor(item.id)

  useEffect(() => {
    if (!item.email) return
    setSrc(null)
    setImgFailed(false)
    void gravatarUrl(item.email, size * 2).then(setSrc)
  }, [item.email, size])

  return (
    <Tooltip content={item.email ? `${item.name} (${item.email})` : item.name} delayMs={150}>
      <div
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: bg,
          color: '#fff',
          fontSize: size * 0.4,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginLeft: index === 0 ? 0 : -size * 0.3,
          zIndex: max - index,
          position: 'relative',
          boxShadow: `0 0 0 2px ${ringColor}`,
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        {initials(item.name)}
        {src && !imgFailed && (
          <img
            src={src}
            alt=""
            onError={() => setImgFailed(true)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </div>
    </Tooltip>
  )
}

function PopoverItem({ item }: { item: AvatarStackItem }) {
  const [src, setSrc] = useState<string | null>(null)
  const [imgFailed, setImgFailed] = useState(false)
  const bg = item.color ?? avatarColor(item.id)

  useEffect(() => {
    if (!item.email) return
    setSrc(null)
    setImgFailed(false)
    void gravatarUrl(item.email, 56).then(setSrc)
  }, [item.email])

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}>
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: bg,
          color: '#fff',
          fontSize: 11,
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {initials(item.name)}
        {src && !imgFailed && (
          <img
            src={src}
            alt=""
            onError={() => setImgFailed(true)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
          />
        )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.name}
        </div>
        {item.email && (
          <div style={{ fontSize: 10, color: 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {item.email}
          </div>
        )}
      </div>
    </div>
  )
}

export function AvatarStack({
  items,
  max = 5,
  size = 24,
  showPopover = false,
  ringColor = 'var(--bg-card)',
  totalCount,
}: AvatarStackProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const visible = items.slice(0, max)
  const realTotal = totalCount ?? items.length
  const extra = realTotal - max

  useEffect(() => {
    if (!open || !showPopover) return
    const handler = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', handler)
    return () => document.removeEventListener('pointerdown', handler)
  }, [open, showPopover])

  const stack = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {visible.map((item, i) => (
        <StackItem key={item.id} item={item} size={size} ringColor={ringColor} index={i} max={max} />
      ))}
      {extra > 0 && (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: 'var(--border)',
            color: 'var(--text-muted)',
            fontSize: size * 0.37,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: -size * 0.3,
            zIndex: 0,
            position: 'relative',
            boxShadow: `0 0 0 2px ${ringColor}`,
            flexShrink: 0,
          }}
        >
          +{extra}
        </div>
      )}
    </div>
  )

  if (!showPopover) return stack

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', display: 'flex' }}
      >
        {stack}
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: 0,
            zIndex: 50,
            minWidth: 180,
            background: 'var(--bg-popup)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
            padding: '6px 0',
          }}
        >
          {items.map((item) => (
            <PopoverItem key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  )
}
